import type { Scenario } from '../types';

export const ENDING: Scenario = {
  id: 'ending',
  title: 'the ending',
  brief: 'you want to end a dynamic with someone you have cared deeply about. you also still care about him. you are about to tell him.',
  skill: 'grace, clarity, and self-trust under emotional weight',
  estMinutes: 6,
  startNode: 'open',
  nodes: {
    open: {
      id: 'open',
      paragraphs: [
        { text: 'you asked to meet. he is sitting across from you. he doesn\'t know yet, but the way you held your hands when you asked to meet may have told him something.', speaker: 'narration' },
        { text: '"what is it?"', speaker: 'them' },
      ],
      choices: [
        { text: 'say it plainly, right at the top.', nextNode: 'plain-open' },
        { text: 'start by naming what you have valued.', nextNode: 'valued-open' },
        { text: 'name the texture: "this is hard for me to say."', nextNode: 'texture-open' },
      ],
    },
    'plain-open': {
      id: 'plain-open',
      paragraphs: [
        { text: '"i need to end the dynamic." you do not soften it. you do not bury it.', speaker: 'you' },
        { text: 'he hears it. you watch the words land. you do not look away.', speaker: 'narration' },
        { text: 'after a long moment: "okay. tell me."', speaker: 'them' },
      ],
      choices: [
        { text: 'tell him why. specifically. without listing his faults.', nextNode: 'why-specific' },
        { text: '"i don\'t want to make it about reasons. i want to make it about what i need."', nextNode: 'about-need' },
      ],
    },
    'valued-open': {
      id: 'valued-open',
      paragraphs: [
        { text: 'you tell him what you valued. you do not lie. the things you say are true.', speaker: 'narration' },
        { text: 'he listens with care. and then, gently: "you are not here to tell me what you valued. tell me what you came to tell me."', speaker: 'them' },
      ],
      choices: [
        { text: 'say it. "i need to end the dynamic."', nextNode: 'plain-open' },
      ],
    },
    'texture-open': {
      id: 'texture-open',
      paragraphs: [
        { text: '"this is hard for me to say. i want you to know that before i say it."', speaker: 'you' },
        { text: 'he nods. he waits. he gives you the space to say it.', speaker: 'narration' },
      ],
      choices: [
        { text: 'say it.', nextNode: 'plain-open' },
      ],
    },
    'why-specific': {
      id: 'why-specific',
      paragraphs: [
        { text: 'you tell him. you talk about what you have noticed in yourself, not in him. you talk about the shape your life needs to take.', speaker: 'narration' },
        { text: 'he listens. when you finish, he is quiet for a long moment.', speaker: 'narration' },
        { text: '"thank you for the specificity. that\'s a kindness."', speaker: 'them' },
      ],
      choices: [
        { text: 'ask if there is anything he wants to say.', nextNode: 'his-response' },
        { text: 'name the shape of what comes next.', nextNode: 'logistics' },
      ],
    },
    'about-need': {
      id: 'about-need',
      paragraphs: [
        { text: '"okay," he says. "tell me what you need."', speaker: 'them' },
      ],
      choices: [
        { text: 'tell him what you need — what no contact looks like, or what staying in touch would mean.', nextNode: 'logistics' },
      ],
    },
    'his-response': {
      id: 'his-response',
      paragraphs: [
        { text: 'he takes his time. you can feel him deciding what is true and what is for his benefit to say.', speaker: 'narration' },
        { text: '"i want to argue with you," he says, after a while. "and i\'m not going to. you\'ve thought about this. you would have already changed your mind if there was a version of staying that worked for you."', speaker: 'them' },
        { text: 'the relief you feel at his not arguing tells you he is right.', speaker: 'narration' },
      ],
      choices: [
        { text: 'thank him for not arguing.', nextNode: 'logistics' },
        { text: 'sit in the silence with him.', nextNode: 'silence-with' },
      ],
    },
    logistics: {
      id: 'logistics',
      paragraphs: [
        { text: 'you talk through what comes next. a return of his things, a question about who tells whom, a window of no contact.', speaker: 'narration' },
        { text: 'the conversation is short. neither of you tries to extend it.', speaker: 'narration' },
      ],
      choices: [
        { text: 'thank him for the dynamic you had.', nextNode: 'gratitude' },
        { text: 'stand up. leave.', nextNode: 'leave' },
      ],
    },
    'silence-with': {
      id: 'silence-with',
      paragraphs: [
        { text: 'the silence holds. it is not heavy. it is the kind of silence that has space in it.', speaker: 'narration' },
        { text: 'after a while you both notice that you are not crying. you are both just here.', speaker: 'narration' },
      ],
      choices: [
        { text: 'eventually, talk about what comes next.', nextNode: 'logistics' },
      ],
    },
    gratitude: {
      id: 'gratitude',
      paragraphs: [
        { text: 'you tell him, briefly and without performing, that the dynamic mattered. you do not make it about yourself.', speaker: 'narration' },
        { text: '"it mattered to me too," he says.', speaker: 'them' },
        { text: 'you both stand. you do not hug. there is a small bow you do not make, but that is in the air anyway.', speaker: 'narration' },
      ],
      choices: [
        { text: 'leave.', nextNode: 'leave' },
      ],
    },
    leave: {
      id: 'leave',
      paragraphs: [
        { text: 'outside the cafe, the air is colder than you remembered. you walk for a while before you call anyone.', speaker: 'narration' },
        { text: 'you have done a hard thing well. it will be hard for a long time. it was still the right thing.', speaker: 'narration' },
      ],
    },
  },
  reflection: {
    title: 'on endings that are not arguments',
    skillPracticed: 'closing a dynamic clearly without making the partner persuade you to stay',
    body: 'endings often become re-negotiations because the person ending it is afraid of the weight of having decided. the discipline is to arrive having decided, and to communicate the decision without performing the decision. the partner does not need to agree with the ending for the ending to be valid. grace is in the form: clear, specific about your own experience, generous about what was real, without the audit of his shortcomings. the dynamic can end and the care can survive. they are not the same thing.',
    otherPaths: [
      'you could have ended in writing. some endings are kinder in writing — they spare both people the in-the-moment improvisation. there is no rule that all endings must be face-to-face.',
      'you could have asked for a pause rather than an end, if pause was what you actually meant. naming the actual shape is part of the kindness.',
      'you could have brought a friend to wait nearby — not as a witness to the conversation, but as someone for after. ending a dynamic is hard. you do not have to do the after part alone.',
    ],
  },
};
